import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import EventsEditModal from './EventsEditModal'; // Importa el nuevo modal
import DeleteModal from '../../components/DeleteModal/DeleteModal';

const columnHelper = createColumnHelper();

export default function EventsTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  handleUpdateEvent,
  sorting,
  onSortingChange,
}) {
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [modalState, setModalState] = React.useState({ isOpen: false });
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [eventToDelete, setEventToDelete] = React.useState(null);
  const [deleteModalState, setDeleteModalState] = React.useState({
    isOpen: false,
  });
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData],
  );

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  const openEditModal = (rowData) => {
    setSelectedEvent(rowData);
    setModalState({ isOpen: true });
  };

  const openDeleteModal = (rowData) => {
    setEventToDelete(rowData);
    setDeleteModalState({ isOpen: true });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false });
    setSelectedEvent(null);
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false });
    setEventToDelete(null);
  };

  const handleDelete = () => {
    const updatedEvent = { ...eventToDelete, enabled: !eventToDelete.enabled };
    handleUpdateEvent(updatedEvent);
    closeDeleteModal();
  };

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        size: col.width ? parseInt(col.width, 10) : undefined,
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => (
          <Flex align="center">
            {!col.actions && (
              <Text fontSize="sm" fontWeight="normal">
                {info.getValue()}
              </Text>
            )}
            {col.actions &&
              col.actions.map((action, index) => (
                <IconButton
                  key={index}
                  aria-label={action.label}
                  icon={action.icon}
                  size="sm"
                  variant="ghost"
                  colorScheme={action.colorScheme}
                  ml={2}
                  onClick={() => {
                    if (action.type === 'edit') {
                      openEditModal(info.row.original);
                    } else if (action.type === 'delete') {
                      openDeleteModal(info.row.original);
                    }
                  }}
                />
              ))}
          </Flex>
        ),
        meta: {
          width: col.width,
          isActions: Boolean(col.actions),
        },
      }),
    );
  }

  return (
    <PageTable
      flexDirection="column"
      w="100%"
      px="0px"
     >
      <Box>
        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px" w="100%">
                    <Thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <SortableTableHeader
                              key={header.id}
                              header={header}
                              borderColor={borderColor}
                            />
                          ))}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <AdminTableCell
                              key={cell.id}
                              cell={cell}
                              whiteSpace={
                                cell.column.id === 'name' ? 'normal' : undefined
                              }
                            />
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
        </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
      </Box>

      <EventsEditModal
        isOpen={modalState.isOpen}
        onClose={closeEditModal}
        event={selectedEvent}
        onUpdate={handleUpdateEvent}
      />
      <DeleteModal
        isOpen={deleteModalState.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        name={`${eventToDelete?.code} - ${eventToDelete?.name}`}
      />
    </PageTable>
  );
}
